<template>
  <div class="h-full bg-[#f8fafc] p-4 overflow-y-auto custom-scroll">
    <div class="print-hide bg-white rounded-xl shadow-sm border border-slate-200 border-t-4 border-t-[#0d4169] transition-all overflow-hidden mb-6">
      <div class="px-6 pt-5 pb-3">
        <h2 class="text-base font-bold text-[#0d4169]">Debone Report</h2>
      </div>

      <form @submit.prevent="generateReport" class="px-6 pb-6 pt-2 border-b border-slate-100">
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-3">
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">
              Start Date <span class="text-red-500">*</span>
            </label>
            <input
              v-model="filters.startDate"
              required
              type="date"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            />
          </div>

          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">End Date</label>
            <input
              v-model="filters.endDate"
              type="date"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            />
          </div>

          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Report Type</label>
            <select
              v-model="filters.reportType"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer hover:bg-slate-50"
            >
              <option value="Summary">Summary</option>
              <option value="Detail">Detail</option>
            </select>
          </div>

          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Department</label>
            <select
              v-model="filters.departmentId"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer hover:bg-slate-50"
            >
              <option disabled selected :value="null">Select Department</option>
              <option :value="NO_DEPARTMENT">No Department</option>
              <option v-for="dep in departments" :key="dep.id" :value="String(dep.id)">
                {{ dep.name }}
              </option>
            </select>
          </div>

          <div class="flex flex-col gap-1 text-left lg:col-span-2">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Search By Item</label>
            <input
              v-model="filters.searchItem"
              type="text"
              placeholder="Enter ingredient name..."
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            />
          </div>
        </div>

        <div class="mt-6 flex justify-between items-center gap-4">
          <button
            type="button"
            @click="handlePrintReport"
            class="px-4 py-1.5 bg-slate-200 text-slate-700 text-sm font-bold rounded-md shadow-md hover:bg-slate-300 transition-all active:scale-95 disabled:opacity-60 flex items-center gap-2"
            :disabled="loading"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M6 9V2h12v7"/>
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
              <rect x="6" y="14" width="12" height="8"/>
            </svg>
            <span>Print</span>
          </button>
          <button
            type="submit"
            class="px-8 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 disabled:opacity-60 flex items-center gap-2"
            :disabled="!filters.startDate || loading"
          >
            <span v-if="loading" class="w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin"></span>
            <span>{{ loading ? '' : 'Generate' }}</span>
          </button>
        </div>
      </form>
    </div>

    <div class="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden pr-3 pl-3 print-container">
      <div class="hidden print:block mb-6">
        <div class="print-doc-header print-doc-header-grid">
          <div class="print-doc-left">
            <div class="print-company-block">
              <img
                v-if="printCompanyLogo"
                :src="printCompanyLogo"
                :alt="printCompanyName || 'Company Logo'"
                class="print-company-logo"
              />
              <div v-else class="print-company-logo-fallback">Logo</div>
            </div>
          </div>
          <div class="print-doc-title-wrap">
            <div class="print-company-name print-company-name-title">{{ printCompanyName }}</div>
            <h1 class="print-doc-title text-xl font-bold text-center mb-2">Debone Report</h1>
          </div>
          <div class="print-doc-meta print-doc-right">
            <div><strong>Date:</strong> {{ new Date().toLocaleDateString() }}</div>
          </div>
        </div>
        <hr class="my-4 border-black" />
      </div>

      <div class="relative overflow-auto min-h-[220px] max-h-[calc(100vh-260px)]">
        <table class="w-full debone-report-table min-w-max print-table">
          <thead>
            <tr>
              <th rowspan="2" class="w-10 text-center sticky-col bg-[#f1f5f9] no-side-border">No.</th>
              <th
                v-if="filters.reportType === 'Detail'"
                rowspan="2"
                class="w-24 text-center bg-[#f1f5f9]"
              >
                Date
              </th>
              <th rowspan="2" class="min-w-[120px] sticky-col bg-[#f1f5f9] no-side-border col-ingredient-name" :class="filters.reportType === 'Detail' ? 'sticky-ingredient-detail' : 'sticky-ingredient-summary'">Ingredient Name</th>
              <th colspan="2" class="text-center bg-[#f1f5f9] qty-group-header">Bbf</th>
              <th colspan="2" class="text-center bg-[#f1f5f9] qty-group-header">Received Store</th>
              <th colspan="2" class="text-center bg-[#f1f5f9] qty-group-header">Received Non.Stockable</th>
              <th colspan="2" class="text-center bg-[#f1f5f9] qty-group-header">Product</th>
              <th rowspan="2" class="w-24 text-center">Transfer Recived (D)</th>
              <th colspan="2" class="text-center bg-[#f1f5f9] qty-group-header">Transfer Recived (T)</th>
              <th colspan="2" class="text-center bg-[#f1f5f9] qty-group-header">Wastage</th>
              <th colspan="2" class="text-center bg-slate-100 qty-group-header font-bold">Total On Hand</th>
              <th rowspan="2" class="w-24 text-center">Send To</th>
              <th colspan="2" class="text-center bg-[#f1f5f9] qty-group-header">Total Distributed</th>
              <th colspan="2" class="text-center bg-[#f1f5f9] qty-group-header">Spoiled By Items</th>
              <th colspan="2" class="text-center bg-[#f1f5f9] qty-group-header">Other/Catering</th>
              <th colspan="2" class="text-center bg-blue-50 qty-group-header font-bold">Total Active</th>
              <th colspan="2" class="text-center bg-[#f1f5f9] qty-group-header">Balance</th>
              <th colspan="2" class="text-center bg-[#f1f5f9] qty-group-header">Ending</th>
              <th colspan="2" class="text-center bg-red-50 qty-group-header font-bold text-red-700">Deference</th>
            </tr>
            <tr>
              <template v-for="n in 14" :key="`qty-sub-${n}`">
                <th class="w-20 text-center bg-[#f1f5f9] qty-sub-header">PC</th>
                <th class="w-20 text-center bg-[#f1f5f9] qty-sub-header">Kg/L</th>
              </template>
            </tr>
          </thead>
          <tbody>
            <tr v-if="rows.length === 0">
              <td :colspan="emptyColspan" class="px-4 py-24 text-center text-slate-400 italic text-sm">
                <div class="text-5xl mb-4 opacity-20">📄</div>
                No debone control records to display.<br />
                Select filters and click <span class="text-[#0d4169] font-bold">"Generate"</span>
              </td>
            </tr>
            <tr
              v-for="(row, index) in rows"
              :key="rowKey(row, index)"
              class="hover:bg-slate-50 transition-colors group border-b last:border-0"
            >
              <td class="text-center sticky-col bg-white group-hover:bg-slate-50 no-side-border">{{ index + 1 }}</td>
              <td
                v-if="filters.reportType === 'Detail'"
                class="bg-slate-50/50 text-slate-600 text-sm text-center"
              >
                {{ formatDisplayDate(row.calendar) }}
              </td>
              <td
                class="sticky-col bg-white group-hover:bg-slate-50 no-side-border font-medium col-ingredient-name"
                :class="[
                  isDeboneKind(row.kind || row.type) ? 'text-green-600' : '',
                  filters.reportType === 'Detail' ? 'sticky-ingredient-detail' : 'sticky-ingredient-summary'
                ]"
              >
                {{ row.ingredientName || '—' }}
              </td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ displayNum(row.bbfPc) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ displayNum(row.bbfKgL) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ displayNum(row.storeRecvPc) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ displayNum(row.storeRecvKgL) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ displayNum(row.nonStockPc) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ displayNum(row.nonStockKgL) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ displayNum(row.productPc) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ displayNum(row.productKgL) }}</td>
              <td class="text-center text-xs text-slate-600">{{ formatTransInSummary(row) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm font-bold text-center">{{ displayNum(row.transIn1Pc) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm font-bold text-center">{{ displayNum(row.transIn1KgL) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ displayNum(row.wastagePc) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ displayNum(row.wastageKgL) }}</td>
              <td class="font-black text-[#0d4169] text-center bg-slate-50/30">{{ displayNum(row.totalOnHandPc) }}</td>
              <td class="font-black text-[#0d4169] text-center bg-slate-50/30">{{ displayNum(row.totalOnHandKgL) }}</td>
              <td class="text-center text-xs text-slate-600">{{ formatSendToSummary(row) }}</td>
              <td class="font-black text-[#0d4169] text-center bg-slate-50/30">{{ displayNum(row.totalDistributedPc) }}</td>
              <td class="font-black text-[#0d4169] text-center bg-slate-50/30">{{ displayNum(row.totalDistributedKgL) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ displayNum(row.spoiledItemPc) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ displayNum(row.spoiledItemKgL) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ displayNum(row.cateringPc) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ displayNum(row.cateringKgL) }}</td>
              <td class="font-black text-[#0d4169] text-center bg-slate-50/30">{{ displayNum(row.totalActivePc) }}</td>
              <td class="font-black text-[#0d4169] text-center bg-slate-50/30">{{ displayNum(row.totalActiveKgL) }}</td>
              <td class="font-black text-green-700 text-center">{{ displayNum(row.balancePc) }}</td>
              <td class="font-black text-green-700 text-center">{{ displayNum(row.balanceKgL) }}</td>
              <td class="bg-slate-50/50 text-slate-500 text-sm text-center">{{ displayNum(row.endingPc) }}</td>
              <td class="bg-slate-50/50 text-slate-500 text-sm text-center">{{ displayNum(row.endingKgL) }}</td>
              <td
                class="font-black text-center bg-red-50/30"
                :class="{ 'text-red-500': Number(row.variancePc) < 0, 'text-green-500': Number(row.variancePc) > 0 }"
              >{{ displayNum(row.variancePc) }}</td>
              <td
                class="font-black text-center bg-red-50/30"
                :class="{ 'text-red-500': Number(row.varianceKgL) < 0, 'text-green-500': Number(row.varianceKgL) > 0 }"
              >{{ displayNum(row.varianceKgL) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import store from '../../store'
import { apiFetch } from '../../utils/api'

const NO_DEPARTMENT = 'none'

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || currentUser.value?.company?.id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || currentUser.value?.branch?.id || null)
const printCompanyName = computed(() =>
  currentUser.value?.company?.name ||
  currentUser.value?.companyName ||
  currentUser.value?.company_name ||
  'Company'
)
const printCompanyLogo = computed(() =>
  currentUser.value?.company?.logo_url ||
  currentUser.value?.companyLogo ||
  currentUser.value?.company_logo ||
  ''
)

const departments = ref([])
const rows = ref([])
const loading = ref(false)

const filters = ref({
  startDate: '',
  endDate: '',
  reportType: 'Summary',
  departmentId: null,
  searchItem: '',
})

const emptyColspan = computed(() => (filters.value.reportType === 'Detail' ? 33 : 32))

function formatDisplayDate(isoDate) {
  if (!isoDate) return '—'
  const d = new Date(`${isoDate}T00:00:00`)
  if (Number.isNaN(d.getTime())) return isoDate
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

function displayNum(val) {
  const num = Number(val || 0)
  return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 5 })
}

function isDeboneKind(kind) {
  const k = String(kind || '').toLowerCase()
  return k === 'debone' || k === 'product'
}

function rowQty(row, field, unit) {
  const key = unit === 'pc' ? `${field}Pc` : `${field}KgL`
  return Number(row[key]) || 0
}

function productContributionForTotalOnHand(row, unit) {
  const prod = rowQty(row, 'product', unit)
  return row.kind === 'item' ? -prod : prod
}

function computeDerived(row) {
  row.totalOnHandPc = (
    rowQty(row, 'bbf', 'pc') +
    rowQty(row, 'storeRecv', 'pc') +
    rowQty(row, 'nonStock', 'pc') +
    productContributionForTotalOnHand(row, 'pc') +
    rowQty(row, 'transIn1', 'pc') -
    rowQty(row, 'wastage', 'pc')
  ).toFixed(2)

  row.totalOnHandKgL = (
    rowQty(row, 'bbf', 'kgl') +
    rowQty(row, 'storeRecv', 'kgl') +
    rowQty(row, 'nonStock', 'kgl') +
    productContributionForTotalOnHand(row, 'kgl') +
    rowQty(row, 'transIn1', 'kgl') -
    rowQty(row, 'wastage', 'kgl')
  ).toFixed(2)

  row.totalActivePc = (
    rowQty(row, 'totalDistributed', 'pc') +
    rowQty(row, 'spoiledItem', 'pc') +
    rowQty(row, 'catering', 'pc')
  ).toFixed(2)

  row.totalActiveKgL = (
    rowQty(row, 'totalDistributed', 'kgl') +
    rowQty(row, 'spoiledItem', 'kgl') +
    rowQty(row, 'catering', 'kgl')
  ).toFixed(2)

  row.balancePc = (Number(row.totalOnHandPc) - Number(row.totalActivePc)).toFixed(2)
  row.balanceKgL = (Number(row.totalOnHandKgL) - Number(row.totalActiveKgL)).toFixed(2)
  row.variancePc = (rowQty(row, 'ending', 'pc') - Number(row.balancePc)).toFixed(2)
  row.varianceKgL = (rowQty(row, 'ending', 'kgl') - Number(row.balanceKgL)).toFixed(2)
  return row
}

function normalizeSavedRow(raw, calendar = null, preserveSavedDerived = true) {
  const type = String(raw?.type || raw?.kind || 'item').toLowerCase()
  const kind = type === 'debone' || type === 'product' ? 'debone' : 'item'
  const row = {
    calendar: calendar || raw?.calendar || raw?.date || null,
    type: kind,
    kind,
    itemId: raw?.itemId ?? raw?.item_id ?? null,
    deboneRegistrationId: raw?.deboneRegistrationId ?? raw?.debone_registration_id ?? null,
    ingredientName: raw?.ingredientName || raw?.ingredient_name || '-',
    bbfPc: Number(raw?.bbfPc ?? 0),
    bbfKgL: Number(raw?.bbfKgL ?? 0),
    storeRecvPc: Number(raw?.storeRecvPc ?? 0),
    storeRecvKgL: Number(raw?.storeRecvKgL ?? 0),
    nonStockPc: Number(raw?.nonStockPc ?? 0),
    nonStockKgL: Number(raw?.nonStockKgL ?? 0),
    productPc: Number(raw?.productPc ?? raw?.product_pc ?? 0),
    productKgL: Number(raw?.productKgL ?? raw?.product_kgl ?? 0),
    transInDetails: Array.isArray(raw?.transInDetails) ? raw.transInDetails : [],
    transIn1Pc: Number(raw?.transIn1Pc ?? 0),
    transIn1KgL: Number(raw?.transIn1KgL ?? 0),
    wastagePc: Number(raw?.wastagePc ?? 0),
    wastageKgL: Number(raw?.wastageKgL ?? 0),
    sendToDetails: Array.isArray(raw?.sendToDetails) ? raw.sendToDetails : [],
    totalDistributedPc: Number(raw?.totalDistributedPc ?? 0),
    totalDistributedKgL: Number(raw?.totalDistributedKgL ?? 0),
    spoiledItemPc: Number(raw?.spoiledItemPc ?? 0),
    spoiledItemKgL: Number(raw?.spoiledItemKgL ?? 0),
    cateringPc: Number(raw?.cateringPc ?? 0),
    cateringKgL: Number(raw?.cateringKgL ?? 0),
    endingPc: Number(raw?.endingPc ?? 0),
    endingKgL: Number(raw?.endingKgL ?? 0),
    totalOnHandPc: Number(raw?.totalOnHandPc ?? 0),
    totalOnHandKgL: Number(raw?.totalOnHandKgL ?? 0),
    totalActivePc: Number(raw?.totalActivePc ?? 0),
    totalActiveKgL: Number(raw?.totalActiveKgL ?? 0),
    balancePc: Number(raw?.balancePc ?? 0),
    balanceKgL: Number(raw?.balanceKgL ?? 0),
    variancePc: Number(raw?.variancePc ?? 0),
    varianceKgL: Number(raw?.varianceKgL ?? 0),
  }

  if (preserveSavedDerived) {
    return row
  }
  return computeDerived(row)
}

function getRowKey(row) {
  const type = String(row?.type || row?.kind || 'item').toLowerCase()
  if (type === 'item' && row?.itemId) return `item_${row.itemId}`
  if ((type === 'debone' || type === 'product') && row?.deboneRegistrationId) {
    return `debone_${row.deboneRegistrationId}`
  }
  return `${type}_name_${String(row?.ingredientName || '').trim().toLowerCase()}`
}

function rowKey(row, index) {
  if (filters.value.reportType === 'Detail') {
    return `${row.calendar || ''}-${getRowKey(row)}-${index}`
  }
  return `${getRowKey(row)}-${index}`
}

function formatTransInSummary(row) {
  const details = Array.isArray(row?.transInDetails) ? row.transInDetails : []
  if (!details.length) return '-'
  return `${details.length} dept(s)`
}

function formatSendToSummary(row) {
  const pc = Number(row?.totalDistributedPc) || 0
  const kgl = Number(row?.totalDistributedKgL) || 0
  if (pc <= 0 && kgl <= 0) return '-'
  return `PC ${displayNum(pc)} · Kg/L ${displayNum(kgl)}`
}

function applySearchFilter(list) {
  const term = String(filters.value.searchItem || '').trim().toLowerCase()
  if (!term) return list
  return list.filter((row) => String(row.ingredientName || '').toLowerCase().includes(term))
}

function aggregateSummary(list, startDate, endDate) {
  const start = String(startDate || '')
  const end = String(endDate || startDate || '')
  const byKey = new Map()

  for (const raw of list) {
    const row = normalizeSavedRow(raw, null, true)
    const key = getRowKey(row)
    if (!byKey.has(key)) byKey.set(key, [])
    byKey.get(key).push(row)
  }

  const out = []
  for (const arr of byKey.values()) {
    const sorted = [...arr].sort((a, b) => String(a.calendar || '').localeCompare(String(b.calendar || '')))
    const startRow = sorted.find((x) => String(x.calendar || '') === start) || sorted[0]
    const endRow = sorted.slice().reverse().find((x) => String(x.calendar || '') === end) || sorted[sorted.length - 1]

    const base = {
      calendar: null,
      type: sorted[0]?.type || 'item',
      kind: sorted[0]?.kind || sorted[0]?.type || 'item',
      ingredientName: sorted[0]?.ingredientName || '-',
      itemId: sorted[0]?.itemId ?? null,
      deboneRegistrationId: sorted[0]?.deboneRegistrationId ?? null,
      bbfPc: Number(startRow?.bbfPc ?? 0),
      bbfKgL: Number(startRow?.bbfKgL ?? 0),
      endingPc: Number(endRow?.endingPc ?? 0),
      endingKgL: Number(endRow?.endingKgL ?? 0),
      storeRecvPc: 0,
      storeRecvKgL: 0,
      nonStockPc: 0,
      nonStockKgL: 0,
      productPc: 0,
      productKgL: 0,
      transInDetails: [],
      transIn1Pc: 0,
      transIn1KgL: 0,
      wastagePc: 0,
      wastageKgL: 0,
      sendToDetails: [],
      totalDistributedPc: 0,
      totalDistributedKgL: 0,
      spoiledItemPc: 0,
      spoiledItemKgL: 0,
      cateringPc: 0,
      cateringKgL: 0,
      totalOnHandPc: 0,
      totalOnHandKgL: 0,
      totalActivePc: 0,
      totalActiveKgL: 0,
      balancePc: 0,
      balanceKgL: 0,
      variancePc: 0,
      varianceKgL: 0,
    }

    for (const r of sorted) {
      base.storeRecvPc += Number(r.storeRecvPc) || 0
      base.storeRecvKgL += Number(r.storeRecvKgL) || 0
      base.nonStockPc += Number(r.nonStockPc) || 0
      base.nonStockKgL += Number(r.nonStockKgL) || 0
      base.productPc += Number(r.productPc) || 0
      base.productKgL += Number(r.productKgL) || 0
      base.transIn1Pc += Number(r.transIn1Pc) || 0
      base.transIn1KgL += Number(r.transIn1KgL) || 0
      base.wastagePc += Number(r.wastagePc) || 0
      base.wastageKgL += Number(r.wastageKgL) || 0
      base.totalDistributedPc += Number(r.totalDistributedPc) || 0
      base.totalDistributedKgL += Number(r.totalDistributedKgL) || 0
      base.spoiledItemPc += Number(r.spoiledItemPc) || 0
      base.spoiledItemKgL += Number(r.spoiledItemKgL) || 0
      base.cateringPc += Number(r.cateringPc) || 0
      base.cateringKgL += Number(r.cateringKgL) || 0
      base.totalOnHandPc += Number(r.totalOnHandPc) || 0
      base.totalOnHandKgL += Number(r.totalOnHandKgL) || 0
      base.totalActivePc += Number(r.totalActivePc) || 0
      base.totalActiveKgL += Number(r.totalActiveKgL) || 0
      base.balancePc += Number(r.balancePc) || 0
      base.balanceKgL += Number(r.balanceKgL) || 0
      base.variancePc += Number(r.variancePc) || 0
      base.varianceKgL += Number(r.varianceKgL) || 0
    }

    out.push(base)
  }

  return applySearchFilter(out).sort((a, b) =>
    String(a.ingredientName || '').localeCompare(String(b.ingredientName || ''))
  )
}

function buildDateRange(startDate, endDate) {
  const start = String(startDate || '')
  const end = String(endDate || startDate || '')
  const d0 = new Date(`${start}T00:00:00`)
  const d1 = new Date(`${end}T00:00:00`)
  if (Number.isNaN(d0.getTime()) || Number.isNaN(d1.getTime())) {
    throw new Error('Invalid date range')
  }
  if (d1 < d0) {
    throw new Error('End Date must be greater than or equal to Start Date')
  }
  const dates = []
  const cur = new Date(d0)
  while (cur <= d1) {
    const yyyy = cur.getFullYear()
    const mm = String(cur.getMonth() + 1).padStart(2, '0')
    const dd = String(cur.getDate()).padStart(2, '0')
    dates.push(`${yyyy}-${mm}-${dd}`)
    cur.setDate(cur.getDate() + 1)
  }
  return dates
}

function resolveDepartmentIds() {
  if (filters.value.departmentId !== null && filters.value.departmentId !== undefined) {
    return [String(filters.value.departmentId)]
  }
  const ids = [NO_DEPARTMENT]
  for (const dep of departments.value) {
    if (dep?.id != null) ids.push(String(dep.id))
  }
  return ids
}

function handlePrintReport() {
  if (!rows.value.length) {
    alert('No data to print. Please generate the report first.')
    return
  }
  window.print()
}

async function loadDepartments() {
  if (!companyId.value && !branchId.value) return
  try {
    let endpoint = `/departments/company/${companyId.value}`
    if (branchId.value) endpoint = `/departments/branch/${branchId.value}`

    const res = await apiFetch(endpoint)
    if (res.ok) {
      const data = await res.json().catch(() => [])
      departments.value = (Array.isArray(data) ? data : [])
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
    }
  } catch (error) {
    console.error('Failed to load departments', error)
  }
}

async function fetchSavedRowsForRange(departmentIds, startDate, endDate) {
  const query = new URLSearchParams()
  query.append('departmentIds', departmentIds.join(','))
  query.append('startDate', String(startDate))
  query.append('endDate', String(endDate || startDate))
  const res = await apiFetch(`/debones/controls/saved/range?${query.toString()}`)
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err?.message || 'Failed to fetch debone report')
  }
  const payload = await res.json().catch(() => ({ hasData: false, rowsByDepartmentCalendar: {} }))
  return payload?.rowsByDepartmentCalendar && typeof payload.rowsByDepartmentCalendar === 'object'
    ? payload.rowsByDepartmentCalendar
    : {}
}

async function generateReport() {
  if (!filters.value.startDate) return
  loading.value = true
  try {
    const dates = buildDateRange(filters.value.startDate, filters.value.endDate)
    if (!departments.value.length) await loadDepartments()

    const deptIds = resolveDepartmentIds()
    if (deptIds.length === 0) {
      throw new Error('Please select Department')
    }

    const groupedRows = await fetchSavedRowsForRange(
      deptIds,
      filters.value.startDate,
      filters.value.endDate || filters.value.startDate
    )
    const fetched = []
    for (const deptId of deptIds) {
      for (const day of dates) {
        const key = `${String(deptId)}|${day}`
        const dayRows = Array.isArray(groupedRows[key]) ? groupedRows[key] : []
        for (const row of dayRows) {
          fetched.push({ ...row, calendar: row?.calendar || day })
        }
      }
    }

    if (filters.value.reportType === 'Detail') {
      const normalized = fetched.map((row) => normalizeSavedRow(row, row.calendar, true))
      rows.value = applySearchFilter(normalized).sort((a, b) => {
        const dc = String(a.calendar || '').localeCompare(String(b.calendar || ''))
        if (dc !== 0) return dc
        return String(a.ingredientName || '').localeCompare(String(b.ingredientName || ''))
      })
    } else {
      rows.value = aggregateSummary(fetched, filters.value.startDate, filters.value.endDate)
    }
  } catch (error) {
    console.error('Failed to fetch debone report', error)
    alert(error.message || 'Failed to fetch debone report')
    rows.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  filters.value.startDate = ''
  loadDepartments()
})
</script>

<style scoped>
.debone-report-table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  color: #1e293b;
  border-collapse: collapse;
}

.debone-report-table th,
.debone-report-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

.debone-report-table th:nth-child(1),
.debone-report-table td:nth-child(1) {
  text-align: center;
}

.debone-report-table thead th.col-ingredient-name,
.debone-report-table td.col-ingredient-name {
  text-align: left;
}

.debone-report-table th:not(.col-ingredient-name),
.debone-report-table td:not(.col-ingredient-name) {
  text-align: center;
}

.debone-report-table thead th.qty-group-header {
  text-align: center;
}

thead tr:nth-child(2) .qty-sub-header {
  top: 33px;
  z-index: 19;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 20;
}

.sticky-col {
  position: sticky;
  left: 0;
  z-index: 10;
  background-clip: padding-box;
  border-right: 2px solid #e2e8f0 !important;
}

.no-side-border {
  border-right: none !important;
  border-left: none !important;
}

th.sticky-col {
  z-index: 30;
}

.sticky-ingredient-summary {
  left: 40px;
}

.sticky-ingredient-detail {
  left: 136px;
}

@media print {
  :global(.sidebar),
  :global(header),
  :global(nav),
  :global(aside),
  :global(.top-bar),
  :global(.app-header),
  :global(.logout-btn) {
    display: none !important;
  }

  .print-hide { display: none !important; }

  body, .bg-\[\#f8fafc\] {
    background: transparent !important;
  }

  :global(.md\:ml-\[150px\]) {
    margin-left: 0 !important;
  }

  .overflow-auto {
    overflow: visible !important;
    min-height: auto !important;
    max-height: none !important;
  }

  .print-container {
    box-shadow: none !important;
    border: none !important;
    width: 100% !important;
    max-width: 100% !important;
    overflow: visible !important;
  }

  .print-table {
    width: 100% !important;
    min-width: 0 !important;
    border-collapse: collapse !important;
    border-spacing: 0 !important;
    table-layout: auto !important;
  }

  .print-table th, .print-table td {
    border: 0 !important;
    border-right: 1px solid #000 !important;
    border-bottom: 1px solid #000 !important;
    padding: 1px 2px !important;
    text-align: center !important;
    color: #000 !important;
    font-size: 6.8px !important;
    white-space: normal !important;
    word-break: break-word !important;
    overflow-wrap: anywhere !important;
    line-height: 1.15 !important;
  }

  .print-table tr > *:first-child {
    border-left: 1px solid #000 !important;
  }

  .print-table thead tr:first-child > * {
    border-top: 1px solid #000 !important;
  }

  .print-table th {
    background-color: #f3f3f3 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    font-size: 6.2px !important;
    position: static !important;
    top: auto !important;
  }

  .print-table thead {
    position: static !important;
  }

  .print-table thead tr,
  .print-table tbody tr {
    page-break-inside: avoid !important;
  }

  .sticky-col {
    position: static !important;
    left: auto !important;
    z-index: auto !important;
    background: transparent !important;
    min-width: 0 !important;
    width: auto !important;
    max-width: none !important;
  }

  .print-doc-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
  }

  .print-doc-header-grid {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    column-gap: 12px;
  }

  .print-doc-left { justify-self: start; }
  .print-doc-title-wrap {
    justify-self: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
  }
  .print-doc-title {
    justify-self: center;
    margin: 0 !important;
    white-space: nowrap;
  }
  .print-doc-right { justify-self: end; }

  .print-company-block {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .print-company-logo {
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;
    flex: 0 0 52px;
    display: block;
    object-fit: contain;
    border: 0 !important;
    border-radius: 0;
    padding: 0;
    background: #fff;
  }

  .print-company-logo-fallback {
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;
    flex: 0 0 52px;
    border: 1px solid #000;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    color: #334155;
    background: #fff;
  }

  .print-company-name {
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.2;
  }

  .print-company-name-title {
    font-size: 16px;
    text-align: center;
  }

  .print-doc-meta {
    font-size: 12px;
    color: #0f172a;
    white-space: nowrap;
  }
}
</style>
